import { createContext, useContext, useEffect, useState } from 'react'
import { NavLink, Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { farmerApi, farmerSession } from '../lib/farmer'
import { useClock } from '../lib/hooks'
import { IconCash, IconExit, IconHome, IconLeaf, IconScale } from '../ui/kit'
import { FarmerHome } from './FarmerHome'
import { PostHarvest } from './PostHarvest'
import { MyHarvests } from './MyHarvests'
import { MyDeliveries } from './MyDeliveries'

/**
 * The farmer's app. Deliberately the simplest thing in the system: four screens, big
 * type, plain language, and never a number the farmer did not see before they agreed
 * to it. This runs on a cheap Android phone in a field.
 */
export function FarmerShell() {
  const [signedIn, setSignedIn] = useState(farmerSession.isSignedIn)

  useEffect(() => {
    document.documentElement.setAttribute('data-density', 'comfortable')
    return () => document.documentElement.setAttribute('data-density', 'compact')
  }, [])

  // Signing in happens at the shared front door, so there is one entrance to the
  // system rather than a URL each group has to be told about.
  if (!signedIn) return <Navigate to="/login/farmer" replace />

  return <SignedIn onSignOut={() => setSignedIn(false)} />
}

/**
 * Signing out is reachable from every screen, not only the one that happened to have
 * room for it. A shared phone is the normal case here, so getting out has to be as
 * easy as getting in.
 */
const SignOut = createContext<(() => void) | null>(null)

function SignedIn({ onSignOut }: { onSignOut: () => void }) {
  const nav = useNavigate()
  const now = useClock()
  const who = farmerSession.who

  function signOut() {
    if (!window.confirm('Sign out? You will need a new code to get back in.')) return
    farmerApi.signOut()
    onSignOut()
    nav('/login/farmer', { replace: true })
  }

  return (
    <SignOut.Provider value={signOut}>
    <div className="field-shell">
      <div className="field-status">
        <span>{now.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit', hour12: false })}</span>
        <span style={{ fontSize: 10, letterSpacing: '.06em' }}>{who?.name?.toUpperCase()}</span>
      </div>

      <Routes>
        <Route index element={<FarmerHome />} />
        <Route path="post" element={<PostHarvest />} />
        <Route path="harvests" element={<MyHarvests />} />
        <Route path="payments" element={<MyDeliveries />} />
        <Route path="*" element={<Navigate to="/f" replace />} />
      </Routes>

      <nav className="tabbar">
        <FarmerTab to="/f" label="Home" icon={<IconHome size={19} />} end />
        <FarmerTab to="/f/post" label="Post" icon={<IconLeaf size={19} />} />
        <FarmerTab to="/f/harvests" label="Harvests" icon={<IconScale size={19} />} />
        <FarmerTab to="/f/payments" label="Payments" icon={<IconCash size={19} />} />
      </nav>
    </div>
    </SignOut.Provider>
  )
}

function FarmerTab({ to, label, icon, end }: { to: string; label: string; icon: JSX.Element; end?: boolean }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => `tab ${isActive ? 'active' : ''}`}>
      {icon}
      <span>{label}</span>
    </NavLink>
  )
}

/** Shared header for the farmer screens. */
export function FarmerHeader({ title, back }: {
  title?: string
  back?: () => void
}) {
  const signOut = useContext(SignOut)
  return (
    <header className="field-head">
      {back ? (
        <button className="back" onClick={back}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.2" strokeLinecap="round"><path d="M15 5l-7 7 7 7" /></svg>
          {title ?? 'Back'}
        </button>
      ) : (
        <div className="field-brand">
          <span className="mark">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff"
              strokeWidth="2.2" strokeLinecap="round">
              <path d="M12 22V9M12 9c0-4 3-6 8-6 0 5-2.5 8-8 8z" />
              <path d="M12 13c0-3-2-5-6-5 0 4 1.8 6.4 6 6.6z" opacity=".75" />
            </svg>
          </span>
          Ledger
        </div>
      )}
      {signOut ? (
        <button className="header-signout" onClick={signOut}>
          <IconExit size={13} /> Sign out
        </button>
      ) : (
        <span className="pill" style={{ textTransform: 'none' }}>Farmer</span>
      )}
    </header>
  )
}
